import * as React from "react"
import { useContext } from "react"
import styled from "styled-components"
import { formatDate } from "../../util/dateUtil"
import { Icon } from "semantic-ui-react"
import { BasketContext } from "../../context/BasketProvider"
import LinkButton from "../../components/button/LinkButton"
import GLink from "../../../gatsby/GLink"
import {
  AttributeType,
  BasketItemType,
  getFormattedPriceRange,
} from "model"
import { UrlImage } from "components"

const Container = styled.div`
  display: flex;
  width: 100%;
`
const ImageContainer = styled.div<{ smallImage: boolean }>`
  margin-right: 10px;
  max-width: ${({ smallImage }) => (smallImage ? "50px" : "100px")};
`
const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`
const Name = styled.div`
  font-weight: bold;
  font-size: 0.8em;
`
const Price = styled.div`
  font-size: 0.9em;
`
const Attributes = styled.div``
const Attribute = styled.div`
  font-size: 0.8em;
`
const AttributeName = styled.span`
  font-weight: bold;
`
const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: auto;
  width: 100%;
`
const QuantityControl = styled.div`
  i {
    cursor: pointer;
  }
`
const Quantity = styled.span`
  margin: 0 10px;
`
const Remove = styled.div`
  font-size: 0.9em;
`

interface Props {
  item: BasketItemType
  adjustable?: boolean
  smallImage?: boolean
}

const BasketItem = ({ item, adjustable, smallImage = false }: Props) => {
  const attributes = getAttributes(item)
  const basket = useContext(BasketContext)

  return (
    <Container>
      <GLink to={`/product/${item.product.slug}`} onClick={() => basket.toggle(false)}>
        <ImageContainer smallImage={smallImage}>
          <UrlImage image={item.product.image} />
        </ImageContainer>
      </GLink>
      <Content>
        <Name>
          <GLink to={`/product/${item.product.slug}`} onClick={() => basket.toggle(false)}>
            {item.product.name}
          </GLink>
        </Name>
        <Attributes>
          {attributes.map(attribute => (
            <Attribute>
              <AttributeName>{attribute.name}: </AttributeName>
              {attribute.value}
            </Attribute>
          ))}
        </Attributes>
        <Price>
          {getFormattedPriceRange(
            item.product,
            item.variant ? [item.variant] : null,
            item.quantity
          )}
        </Price>

        {adjustable && (
          <Toolbar>
            <QuantityControl>
              <Icon name="minus circle" onClick={() => basket.reduceQuantity(item.key)} />
              <Quantity>{item.quantity}</Quantity>
              <Icon name="plus circle" onClick={() => basket.increaseQuantity(item.key)} />
            </QuantityControl>
            <Remove>
              <LinkButton onClick={() => basket.removeProduct(item.key, item.product)}>
                Remove
              </LinkButton>
            </Remove>
          </Toolbar>
        )}
      </Content>
    </Container>
  )
}

const getAttributes = (item: BasketItemType): AttributeType[] => {
  return item.product.slug === "eco-friendly-gift-card"
    ? getGiftCardAttributes(item)
    : item.variant
    ? item.variant.attributes
    : []
}

const getGiftCardAttributes = (item: BasketItemType): AttributeType[] => {
  const { giftCard } = item
  return [
    {
      name: "From",
      value: giftCard?.name,
    },
    {
      name: "Recipient",
      value: giftCard?.email,
    },
    {
      name: "Send",
      value: giftCard?.schedule === "now" ? "Now" : formatDate(giftCard?.date),
    },
    {
      name: "Note",
      value: giftCard?.note,
    },
  ]
}

export default BasketItem
